import { useState } from "react";
import api, { apiError, brl, parseMoney } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input } from "@/components/ui-kit";

const TITULO = {
  sangria: "Sangria (retirada da gaveta)",
  suprimento: "Suprimento (reforço de troco)",
};

const EXPLICACAO = {
  sangria: "Dinheiro que sai da gaveta durante o turno (depósito, pagamento, troco para outro caixa).",
  suprimento: "Dinheiro que entra na gaveta fora da venda, normalmente para reforçar o troco.",
};

/**
 * Lançamento de sangria/suprimento — e a correção dele.
 *
 * O mesmo diálogo serve para criar e para editar porque a decisão do operador é
 * a mesma nos dois casos ("quanto e por quê"). O que muda é o aviso de que o
 * valor original fica registrado: corrigir um lançamento de caixa não apaga o
 * erro, e quem corrige precisa saber disso antes de confirmar.
 */
export default function MovementDialog({ type, movement, onClose, onDone }) {
  const editando = Boolean(movement);
  const tipo = movement?.kind || type;
  const sangria = tipo === "sangria";
  const ajuste = tipo === "ajuste";

  const [amount, setAmount] = useState(movement ? String(Math.abs(movement.amount)) : "");
  const [reason, setReason] = useState(movement?.description || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const value = parseMoney(amount);
    if (!value || value <= 0) { toast.error("Informe o valor"); return; }
    setSaving(true);
    try {
      if (editando) {
        // O ajuste guarda o próprio sinal; a tela edita em módulo e devolve o
        // sinal que o lançamento já tinha, para uma correção de digitação não
        // virar, sem querer, uma inversão de sentido.
        const assinado = ajuste && movement.amount < 0 ? -value : value;
        await api.patch(`/pos/cash-session/movements/${movement.id}`, { amount: assinado, reason: reason || null });
        toast.success("Lançamento corrigido");
      } else {
        await api.post("/pos/cash-session/movements", { type: tipo, amount: value, reason: reason || null });
        toast.success(sangria ? "Sangria registrada" : "Suprimento registrado");
      }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose}
      title={editando ? `Corrigir ${movement.label.toLowerCase()}` : TITULO[tipo]}
      testId={editando ? "cash-movement-edit-dialog" : `cash-${tipo}-dialog`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          {editando
            ? <>Lançado às {new Date(movement.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} no valor de <b>{brl(Math.abs(movement.amount))}</b>. O valor original continua registrado no extrato.</>
            : EXPLICACAO[tipo]}
        </p>
        <Field label="Valor (R$)" required>
          <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="0,00" data-testid={editando ? "cash-movement-edit-amount" : `cash-${tipo}-amount`} autoFocus mono />
        </Field>
        <Field label="Motivo" hint="Opcional, mas ajuda na conferência do turno.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: depósito no banco" data-testid={editando ? "cash-movement-edit-reason" : `cash-${tipo}-reason`} />
        </Field>
        {/* Sangria em vermelho porque é a única das duas que tira dinheiro da gaveta. */}
        <Button onClick={save} loading={saving} variant={sangria && !editando ? "danger" : "primary"} size="lg" block
          data-testid={editando ? "cash-movement-edit-save" : `cash-${tipo}-save`}>
          {editando ? "Salvar correção" : sangria ? "Registrar sangria" : "Registrar suprimento"}
        </Button>
      </div>
    </Modal>
  );
}
